import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IncadaLocation } from '../../models/incada-location';
import { IncadaService } from '../../services/incada.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import * as constants from 'src/app/config/app-constants';

/**
 * Encapsulates logic for an autocomplete control that selects an Incada location.
 * Toggles between site and org-unit data sources via the orgUnits input.
 * Form value is the location name.
 */
@Component({
    selector: 'app-incada-location-select',
    templateUrl: './incada-location-select.component.html',
    styleUrls: ['./incada-location-select.component.scss'],
    standalone: false
})
export class IncadaLocationSelectComponent implements OnInit, OnChanges, OnDestroy {
  /** The parent form this control is a part of. */
  @Input() form: FormGroup;
  /** Name of the form control to bind to on the parent form. */
  @Input() controlName: string;
  /** Determines whether the backing controls are enabled/disabled. */
  @Input() disabled: boolean;
  /** When true, fetches org-units; when false (default), fetches sites. */
  @Input() orgUnits = false;
  /** Required. The Incada tenant slug to filter locations by. */
  @Input() incadaTenantSlug: string;
  /** Required. The Incada region slug to filter locations by. */
  @Input() incadaRegionSlug: string;
  /** Emits the full IncadaLocation object when a valid location is selected, or null when cleared. */
  @Output() locationSelected = new EventEmitter<IncadaLocation | null>();

  /** Subscription to the service call. */
  private dataSubscription: Subscription;
  /** Subscription to value changes. */
  private valueChangeSubscription: Subscription;
  /** Full list of available locations. */
  locations: IncadaLocation[] = [];
  /** Filtered list of locations to display in autocomplete control. */
  filteredLocations: IncadaLocation[] = [];

  /** The form control associated with this component. */
  get locationControl() { return this.form.get(this.controlName) as FormControl<string>; }

  /** Reference to Validators.required for use in the template. */
  readonly requiredValidator = Validators.required;

  constructor(
    private incadaService: IncadaService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.loadLocations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && this.locationControl) {
      this.disabled ? this.locationControl.disable() : this.locationControl.enable();
    }

    if (changes['orgUnits'] && !changes['orgUnits'].firstChange) {
      this.loadLocations();
    }
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
  }

  private loadLocations(): void {
    this.cleanupSubscriptions();
    this.locations = [];
    this.filteredLocations = [];

    const source$ = this.orgUnits
      ? this.incadaService.getLocationOrgUnits(this.incadaTenantSlug, this.incadaRegionSlug)
      : this.incadaService.getLocationSites(this.incadaTenantSlug, this.incadaRegionSlug);

    this.dataSubscription = source$.subscribe({
      next: (locations) => {
        this.locations = locations;

        const hadRequired = this.locationControl.hasValidator(Validators.required);
        this.locationControl.clearValidators();
        if (hadRequired) {
          this.locationControl.addValidators(Validators.required);
        }
        this.locationControl.addValidators(this.validateLocation().bind(this));

        this.valueChangeSubscription = this.locationControl.valueChanges.subscribe({
          next: value => {
            this.filteredLocations = this.filterLocations(value || '');

            const matched = this.locations.find(l => l.name === value);
            this.locationSelected.emit(matched || null);
          }
        });

        if (this.locationControl.value) {
          this.locationControl.setValue(this.locationControl.value);
        } else {
          this.filteredLocations = this.filterLocations('');
        }
      },
      error: (err) => {
        console.error(err);
        const label = this.orgUnits ? 'org-unit' : 'site';
        this.toastService.openToast(`Error loading Incada ${label} list.`, constants.ToastPanelType.error);
      }
    });
  }

  private cleanupSubscriptions(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
  }

  /** Custom validator for location form data. */
  validateLocation(): ValidatorFn {
    return (control: AbstractControl<string, string>): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      if (!this.locations || this.locations.length === 0) {
        return { NoOptionsError: true };
      }

      if (!this.locations.find(l => l.name === value)) {
        return { InvalidOptionError: true };
      }

      return null;
    }
  }

  private filterLocations(name: string): IncadaLocation[] {
    if (!name) {
      return this.locations;
    }

    return this.locations.filter(l => l.name.toLowerCase().includes(name.toLowerCase()));
  }
}
